import { create } from 'zustand';
import { PlayerEpisode } from './types';

type PlayerState = {
  queue: PlayerEpisode[];
  currentEpisodeId: string | null;
  isPlaying: boolean;
  isBuffering: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isQueueOpen: boolean;
  isFullViewOpen: boolean;
  bufferedDeltaSeconds: number;
  playerError: string | null;
  setQueue: (queue: PlayerEpisode[], currentId: string | null) => void;
  setCurrentEpisodeId: (id: string | null) => void;
  setPlaying: (value: boolean) => void;
  setBuffering: (value: boolean) => void;
  setTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setQueueOpen: (open: boolean) => void;
  setFullViewOpen: (open: boolean) => void;
  setBufferedDelta: (delta: number) => void;
  setError: (message: string | null) => void;
  reset: () => void;
};

const baseState: Omit<
  PlayerState,
  | 'setQueue'
  | 'setCurrentEpisodeId'
  | 'setPlaying'
  | 'setBuffering'
  | 'setTime'
  | 'setDuration'
  | 'setVolume'
  | 'setQueueOpen'
  | 'setFullViewOpen'
  | 'setBufferedDelta'
  | 'setError'
  | 'reset'
> = {
  queue: [],
  currentEpisodeId: null,
  isPlaying: false,
  isBuffering: false,
  currentTime: 0,
  duration: 0,
  volume: 0.9,
  isQueueOpen: false,
  isFullViewOpen: false,
  bufferedDeltaSeconds: 0,
  playerError: null,
};

export const usePlayerStore = create<PlayerState>((set) => ({
  ...baseState,
  setQueue: (queue, currentId) =>
    set((state) => ({
      ...state,
      queue,
      currentEpisodeId: currentId,
    })),
  setCurrentEpisodeId: (id) => set((state) => ({ ...state, currentEpisodeId: id })),
  setPlaying: (value) => set((state) => ({ ...state, isPlaying: value })),
  setBuffering: (value) => set((state) => ({ ...state, isBuffering: value })),
  setTime: (time) => set((state) => ({ ...state, currentTime: time })),
  setDuration: (duration) => set((state) => ({ ...state, duration })),
  setVolume: (volume) => set((state) => ({ ...state, volume })),
  setQueueOpen: (open) => set((state) => ({ ...state, isQueueOpen: open })),
  setFullViewOpen: (open) => set((state) => ({ ...state, isFullViewOpen: open })),
  setBufferedDelta: (delta) => set((state) => ({ ...state, bufferedDeltaSeconds: delta })),
  setError: (message) => set((state) => ({ ...state, playerError: message })),
  reset: () => set({ ...baseState }),
}));
